import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ExternalLink,
  RefreshCw,
  Send,
  Sparkles,
} from 'lucide-react';
import { StatusChip } from '@/components/job-detail/StatusChip';
import { AsyncActionBtn } from '@/components/ui/AsyncActionBtn';
import { Spinner } from '@/components/ui/Spinner';
import { useApplyAttempt } from '@/hooks/useApplyAttempt';
import { useApplyFill } from '@/hooks/useApplyFill';
import { useSubmitApplication } from '@/hooks/useSubmitApplication';

export interface ApplyPanelProps {
  applicationId: number;
  appStatus: string | null;
  jobStatus: string;
}

const liveViewLink = (url: string) => (
  <a
    className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-transparent px-2.5 text-xs lg:text-sm font-medium text-foreground transition-colors hover:bg-surface"
    href={url}
    rel="noopener"
    target="_blank"
  >
    <ExternalLink className="size-3.5" /> Open live view
  </a>
);

export const ApplyPanel = ({ applicationId }: ApplyPanelProps) => {
  const { data: attempt } = useApplyAttempt(applicationId);
  const applyFill = useApplyFill();
  const submitApplication = useSubmitApplication();

  const runFill = () => applyFill.mutateAsync({ applicationId });
  const runSubmit = () => submitApplication.mutateAsync({ applicationId });

  const status = attempt?.status ?? null;

  const vendorLine = attempt && (attempt.ats_vendor || attempt.apply_url) && (
    <p className="font-mono text-2xs lg:text-xs tracking-wider text-subtle uppercase">
      {attempt.ats_vendor ? (
        <>
          Detected ATS ·{' '}
          <span className="text-foreground">{attempt.ats_vendor}</span>
        </>
      ) : (
        'Application form'
      )}
      {attempt.apply_url && (
        <>
          {' · '}
          <a
            className="inline-flex items-center gap-1 text-accent hover:underline"
            href={attempt.apply_url}
            rel="noopener"
            target="_blank"
          >
            view post <ExternalLink className="size-3" />
          </a>
        </>
      )}
    </p>
  );

  // No attempt yet — initial explainer + auto-fill CTA.
  if (!status) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-dashed border-border bg-surface/40 px-5 py-8 text-center">
          <Sparkles className="mx-auto size-5 text-accent" />
          <p className="mx-auto mt-3 max-w-100 text-sm/relaxed text-foreground-muted">
            Auto-fill opens a stealth browser session, fills the ATS application
            form, and pauses for your review before anything is submitted.
          </p>
          <AsyncActionBtn
            animation="pulse"
            className="mt-4"
            icon={Sparkles}
            onAction={runFill}
            size="sm"
            variant="soft"
          >
            Auto-fill application
          </AsyncActionBtn>
        </div>
      </div>
    );
  }

  if (status === 'queued' || status === 'running') {
    return (
      <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-border bg-surface/40 px-5 py-12 text-center">
        <Spinner size="md" />
        <p className="text-sm text-foreground-muted">
          Filling the application…
        </p>
        {attempt?.live_view_url && liveViewLink(attempt.live_view_url)}
      </div>
    );
  }

  if (status === 'submitted') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-score-high/30 bg-score-high/5 px-5 py-12 text-center">
        <CheckCircle2 className="size-6 text-score-high" />
        <p className="text-base font-semibold text-foreground">
          Application submitted
        </p>
        {vendorLine}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <p className="font-mono text-2xs lg:text-xs tracking-wider text-subtle uppercase">
            Auto-fill
          </p>
          <StatusChip status="error" />
        </div>
        <div className="rounded-md border border-score-low/20 bg-score-low/5 p-4">
          <p className="mb-1 font-mono text-xs tracking-wider text-score-low uppercase">
            Auto-fill failed
          </p>
          <p className="text-sm/relaxed text-foreground/90">
            {attempt?.error_message ?? 'An unknown error occurred.'}
          </p>
        </div>
        <AsyncActionBtn
          animation="spin"
          icon={RefreshCw}
          onAction={runFill}
          size="sm"
          variant="soft"
        >
          Try again
        </AsyncActionBtn>
      </div>
    );
  }

  // needs_review
  const filled = attempt?.filled_fields ?? [];
  const unfilled = attempt?.unfilled_fields ?? [];
  const screenshotUrl = attempt?.screenshot_path
    ? `/api/applications/${applicationId}/apply-screenshot?v=${attempt.id}`
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        {vendorLine ?? <span />}
        <StatusChip status="needs_review" />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {attempt?.live_view_url && liveViewLink(attempt.live_view_url)}
      </div>

      {screenshotUrl && (
        <img
          alt="Filled application form preview"
          className="w-full rounded-md border border-border bg-white"
          src={screenshotUrl}
        />
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-md border border-score-high/20 bg-score-high/5 p-3">
          <p className="mb-2 font-mono text-xs tracking-wider text-score-high uppercase">
            Filled
          </p>
          {filled.length === 0 ? (
            <p className="text-sm text-foreground-muted italic">
              No fields filled.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {filled.map((field) => (
                <li
                  className="flex gap-1.5 text-sm text-foreground/90"
                  key={field.label}
                >
                  <Check className="mt-0.5 size-3.5 shrink-0 text-score-high" />
                  <span className="min-w-0">
                    <span className="text-foreground-muted">
                      {field.label}:
                    </span>{' '}
                    {field.value}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="rounded-md border border-score-mid/20 bg-score-mid/5 p-3">
          <p className="mb-2 font-mono text-xs tracking-wider text-score-mid uppercase">
            Needs your attention
          </p>
          {unfilled.length === 0 ? (
            <p className="flex gap-1.5 text-sm text-foreground/90">
              <Check className="mt-0.5 size-3.5 shrink-0 text-score-high" />
              All recognized fields filled.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {unfilled.map((field) => (
                <li
                  className="flex gap-1.5 text-sm text-foreground/90"
                  key={field.label}
                >
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-score-mid" />
                  <span className="min-w-0">
                    <span className="text-foreground">{field.label}</span> —{' '}
                    <span className="text-foreground-muted">
                      {field.reason}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 border-t border-border pt-4">
        <div className="flex flex-wrap items-center gap-1.5">
          <AsyncActionBtn
            animation="spin"
            icon={Send}
            onAction={runSubmit}
            size="sm"
            variant="success"
          >
            Submit application
          </AsyncActionBtn>
          <AsyncActionBtn
            animation="spin"
            icon={RefreshCw}
            onAction={runFill}
            size="sm"
            variant="outline"
          >
            Re-fill
          </AsyncActionBtn>
        </div>
        <p className="text-2xs lg:text-xs text-subtle">
          Submitting finalizes the form in the live browser session — review the
          fields above before you confirm.
        </p>
      </div>
    </div>
  );
};
